import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, AlertCircle, ExternalLink, FileText, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ClientPortalWelcomeProps {
  clientName: string;
  accountSetupCompleted: boolean;
  hasBillingData: boolean;
  isNewUser: boolean;
}

const ClientPortalWelcome: React.FC<ClientPortalWelcomeProps> = ({
  clientName,
  accountSetupCompleted,
  hasBillingData,
  isNewUser
}) => {
  const navigate = useNavigate();

  const getWelcomeMessage = () => {
    if (isNewUser && accountSetupCompleted) {
      return "Welcome to your IT Management Solutions portal! We've imported your existing billing data.";
    }
    if (accountSetupCompleted && hasBillingData) {
      return "Welcome back! Your billing data has been synchronized with your portal.";
    }
    if (accountSetupCompleted) {
      return "Welcome to your IT Management Solutions portal!";
    }
    return "Welcome! Please complete your account setup to get started.";
  };

  const getAccountStatusBadge = () => {
    if (accountSetupCompleted && hasBillingData) {
      return <Badge className="bg-green-500">Fully Activated</Badge>;
    }
    if (accountSetupCompleted) {
      return <Badge className="bg-blue-500">Active</Badge>;
    }
    return <Badge className="bg-yellow-500">Setup Required</Badge>;
  };

  const getNextSteps = () => {
    if (!accountSetupCompleted) {
      return [
        { icon: Clock, text: "Complete account setup", urgent: true },
        { icon: FileText, text: "Review your imported data" },
        { icon: HelpCircle, text: "Take the portal tour" }
      ];
    }
    
    if (hasBillingData) {
      return [
        { icon: CheckCircle, text: "Review recent estimates and invoices" },
        { icon: ExternalLink, text: "Access your NinjaOne portal" },
        { icon: HelpCircle, text: "Explore portal features" }
      ];
    }

    return [
      { icon: FileText, text: "View your services and documents" },
      { icon: ExternalLink, text: "Access your NinjaOne portal" },
      { icon: HelpCircle, text: "Contact support if needed" }
    ];
  };

  const handleCompleteSetup = () => {
    navigate('/setup-account');
  };

  const handleTakePortalTour = () => {
    // Simple tour implementation - highlight key areas
    const tourSteps = [
      { element: '[data-tour="billing-data"]', title: 'Billing Dashboard', description: 'View your estimates, invoices, and billing history here' },
      { element: '[data-tour="ninjaone-portal"]', title: 'NinjaOne Portal', description: 'Access your IT management tools and monitoring' },
      { element: '[data-tour="account-settings"]', title: 'Account Settings', description: 'Update your profile and preferences' }
    ];
    
    // For now, show an alert with tour info
    alert('Portal Tour: This feature highlights key areas of your dashboard. Your billing data is in the cards below, NinjaOne portal access is available in the navigation, and account settings can be found in the top-right menu.');
  };

  const handleViewDocumentation = () => {
    window.open('https://itmgmtsol.com/docs', '_blank');
  };

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl text-blue-800">
            {getWelcomeMessage()}
          </CardTitle>
          {getAccountStatusBadge()}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-blue-800 mb-3">Account Status</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {accountSetupCompleted ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Clock className="h-4 w-4 text-yellow-600" />
                )}
                <span className="text-sm">
                  Account Setup: {accountSetupCompleted ? 'Complete' : 'Required'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {hasBillingData ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-gray-500" />
                )}
                <span className="text-sm">
                  Billing Data: {hasBillingData ? 'Synchronized' : 'Not Available'}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-blue-800 mb-3">Next Steps</h3>
            <div className="space-y-2">
              {getNextSteps().map((step, index) => (
                <div key={index} className="flex items-center gap-2">
                  <step.icon className={`h-4 w-4 ${step.urgent ? 'text-yellow-600' : 'text-blue-600'}`} />
                  <span className={`text-sm ${step.urgent ? 'font-medium text-yellow-800' : 'text-gray-700'}`}>
                    {step.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {isNewUser && hasBillingData && (
          <div className="mt-6 p-4 bg-blue-100 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800">Data Import Complete</h4>
                <p className="text-sm text-blue-700 mt-1">
                  We've successfully imported your existing estimates and invoices from your billing system. 
                  You can now view and manage all your financial documents in one place.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {!accountSetupCompleted && (
            <Button 
              className="bg-yellow-600 hover:bg-yellow-700"
              onClick={handleCompleteSetup}
            >
              Complete Setup
            </Button>
          )}
          <Button variant="outline" onClick={handleTakePortalTour}>
            <HelpCircle className="h-4 w-4 mr-2" />
            Take Portal Tour
          </Button>
          <Button variant="outline" onClick={handleViewDocumentation}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View Documentation
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientPortalWelcome;
